import React, { useState } from "react";
import { Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/LanguageContext";
import { localizedContent } from "@/lib/localize";
import BlockRenderer from "./BlockRenderer";
import PageBuilder from "./PageBuilder";

/*
 * Wraps a public page's body. Non-destructive:
 *  - if the page has content.blocks -> render them (block canvas)
 *  - otherwise -> render the page's original hand-built design (children)
 * When the site language is Spanish the stored translation
 * (content.i18n.es) is layered on first, so Spanish visitors see Spanish
 * blocks. Admin / super_admin get a floating "Edit Page" button that opens
 * the drag-and-drop builder on the copy for the current language.
 */
export default function EditablePage({ slug, page, onSaved, children }) {
  const { user } = useAuth();
  const { lang } = useT();
  const isAdmin = user && (user.role === "admin" || user.role === "super_admin");
  const [editing, setEditing] = useState(false);

  const view = localizedContent(page?.content, lang);
  const blocks = view?.blocks;
  const hasBlocks = Array.isArray(blocks) && blocks.length > 0;

  if (editing) {
    return (
      <PageBuilder
        slug={slug}
        page={page}
        lang={lang}
        onClose={() => setEditing(false)}
        onSaved={(updated) => {
          onSaved?.(updated);
          setEditing(false);
        }}
      />
    );
  }

  return (
    <>
      {hasBlocks ? <BlockRenderer blocks={blocks} /> : children}
      {isAdmin && (
        <button
          onClick={() => setEditing(true)}
          className="fixed bottom-6 right-6 z-50 btn-primary inline-flex items-center gap-2 shadow-lg"
          data-testid="edit-page-fab"
        >
          <Pencil size={16} /> Edit Page
        </button>
      )}
    </>
  );
}
